import { useCallback, useEffect, useState } from 'react'
import { api, type State } from '../api'
import { titleOf } from '../pulse-format'
import { Icon } from './Icon'
import { Confirm } from './Confirm'
import {
  EMPTY_TUNING,
  parseSearchTuning,
  serializeSearchTuning,
  type SearchTuning,
} from '@staleless/schema'

const SEARCH = 'search.yaml'

/**
 * What a reader's search finds, as a form.
 *
 * `search.yaml` has four powers and had one control between them. A gap
 * question on Pulse could teach ONE alias, in one direction, and that was
 * everything: there was no way to see what had already been taught, correct a
 * word taught wrongly, or take one back — and synonyms, pins and weights had
 * no control at all. The file itself had no door either, because the sidebar's
 * whole-site menu lists nav, glossary and style and the tree lists what is
 * under `pages/`. A customer who wanted their pricing page first for "cost"
 * had to commit the file to their repository by hand.
 *
 * All four are here, in the order somebody meets them: the words readers use
 * that these docs do not, the queries that mean another query, the searches
 * with a known right answer, and the pages that are worth more or less
 * everywhere. Written through the same staged path a page uses, so a change to
 * search is reviewed and published with the words it is about — and the file
 * keeps its door at the foot for anybody who would rather write the YAML.
 *
 * Nothing here can hide a page: a weight multiplies a score, so a page can
 * sink and cannot vanish. The screen says so, because a search that silently
 * drops a matching page is a search nobody can debug.
 */
export function SearchScreen({
  state,
  branch,
  readOnly,
  onStaged,
  onOpenPage,
  ensureDraft,
}: {
  state: State
  branch: string | null
  readOnly: boolean
  onStaged: () => void
  /** The file itself, for somebody who would rather write the YAML. */
  onOpenPage: (path: string) => void
  /** The draft this edit needs, made on demand — see the Words screen. */
  ensureDraft?: () => Promise<string | null>
}) {
  const [tuning, setTuning] = useState<SearchTuning | null>(null)
  /** The bytes the file had when it was read, so a write keeps its comments. */
  const [raw, setRaw] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [note, setNote] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [dropping, setDropping] = useState<string | null>(null)

  const [newGroup, setNewGroup] = useState('')
  const [newAliasFrom, setNewAliasFrom] = useState('')
  const [newAliasTo, setNewAliasTo] = useState('')
  const [newPinQuery, setNewPinQuery] = useState('')
  const [newPinPage, setNewPinPage] = useState('')
  const [newBoostPage, setNewBoostPage] = useState('')
  const [newBoostWeight, setNewBoostWeight] = useState('2')

  const load = useCallback(
    async (on: string | null = branch) => {
      /*
       * A missing file is an EMPTY one. Most projects never write this file at
       * all, and a screen reporting "not found" for the ordinary case reads as
       * broken.
       */
      const content = await api
        .page(SEARCH, on ?? undefined)
        .then((page) => page.content)
        .catch(() => '')
      try {
        setRaw(content)
        setTuning(content.trim() === '' ? { ...EMPTY_TUNING } : parseSearchTuning(content))
        setError(null)
      } catch (e) {
        // A file somebody has broken by hand. Say so, and say what it said.
        setTuning({ ...EMPTY_TUNING })
        setError(e instanceof Error ? e.message : String(e))
      }
    },
    [branch],
  )
  useEffect(() => void load(), [load])

  /** Write the file back, on the draft this edit makes if it must. */
  const save = async (next: SearchTuning, done: string) => {
    const on = branch ?? (await ensureDraft?.()) ?? null
    if (!on) return
    setBusy(true)
    setNote(null)
    try {
      /*
       * Serialised through the schema's own writer, which refuses what the
       * parser would refuse — so a mistake is a sentence beside the field
       * rather than a lint message on a file nobody opened.
       */
      const text = serializeSearchTuning(parseSearchTuning(serializeSearchTuning(next, raw)), raw)
      await api.write(on, SEARCH, text)
      await load(on)
      onStaged()
      setError(null)
      setNote(done)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  if (!tuning) {
    return (
      <div className="screen">
        <header className="screen__head">
          <h1>Search</h1>
        </header>
        <div className="screen__loading" aria-busy="true">
          <p className="sr-only">Loading…</p>
          <span className="skeleton" />
          <span className="skeleton" />
        </div>
      </div>
    )
  }

  const pages = state.pages.filter((page) => page.path.startsWith('pages/'))
  /** A page, by the name it has rather than by the file it is in. */
  const nameOf = (path: string) => titleOf(state, path)
  const aliases = Object.entries(tuning.aliases).sort(([a], [b]) => a.localeCompare(b))
  const pinned = Object.entries(tuning.pinned).sort(([a], [b]) => a.localeCompare(b))
  const boosts = Object.entries(tuning.boost).sort(([a], [b]) => a.localeCompare(b))
  const nothingYet =
    tuning.synonyms.length === 0 &&
    aliases.length === 0 &&
    pinned.length === 0 &&
    boosts.length === 0

  /** The picker every page field uses here: by title, never by file path. */
  const pagePicker = (value: string, onPick: (next: string) => void, label: string) => (
    <label className="library__field">
      <span>{label}</span>
      <select
        className="select"
        value={value}
        disabled={busy}
        onChange={(event) => onPick(event.target.value)}
      >
        <option value="">Choose a page…</option>
        {pages.map((page) => (
          <option key={page.path} value={page.path}>
            {nameOf(page.path)}
          </option>
        ))}
      </select>
    </label>
  )

  return (
    <div className="screen" data-search-screen>
      <header className="screen__head">
        <h1>Search</h1>
        <p className="screen__sub">
          What a reader's search finds, when the ranking on its own is not enough. All of it is
          invisible on the page it affects, so this list is the record — and none of it can hide a
          page: a weight moves a page up or down, it never removes it.
        </p>
      </header>

      {readOnly && (
        <p className="screen__warn">
          <Icon name="info" size={13} /> You have view access to this site, so these are read-only.
        </p>
      )}
      {error && (
        <p className="screen__error">
          <Icon name="alert" size={13} /> {error}
        </p>
      )}
      {note && !error && (
        <p className="screen__note" role="status">
          <Icon name="check" size={13} /> {note}
        </p>
      )}
      {nothingYet && !error && (
        <p className="screen__warn" data-search-untuned>
          <Icon name="info" size={13} /> Nothing is tuned yet, and most documentation never needs
          any of it. Pulse says which searches found nothing — that is where the first entry here
          usually comes from.
        </p>
      )}

      <section className="home__card home__card--wide" data-search-synonyms>
        <h2>Words that mean each other</h2>
        <p className="home__quiet">
          A group where every word finds the pages of every other, both ways round. "SSO", "SAML"
          and "single sign-on" are one thing to a reader and three words to a search.
        </p>

        {tuning.synonyms.length === 0 ? (
          <p className="nothing">No groups yet.</p>
        ) : (
          <ul className="vars__list">
            {tuning.synonyms.map((group) => (
              <li className="vars__row" key={group.join('|')} data-synonyms={group.join(',')}>
                <div className="vars__line">
                  <span className="vars__meta">{group.join(' · ')}</span>
                  {!readOnly && (
                    <button
                      className="btn btn--ghost btn--icon btn--sm"
                      disabled={busy}
                      data-tip={`Remove the group ${group.join(', ')}`}
                      onClick={() =>
                        void save(
                          {
                            ...tuning,
                            synonyms: tuning.synonyms.filter(
                              (one) => one.join('|') !== group.join('|'),
                            ),
                          },
                          'That group is gone. Each of those words finds only its own pages now.',
                        )
                      }
                    >
                      <Icon name="trash" size={13} label={`Remove ${group.join(', ')}`} />
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}

        {!readOnly && (
          <form
            className="vars__add"
            onSubmit={(event) => {
              event.preventDefault()
              const words = newGroup
                .split(',')
                .map((word) => word.trim().toLowerCase())
                .filter(Boolean)
              /* Two, because a group of one is a word that means itself. Said
                 before the save rather than after it: the parser refuses this
                 too, and a refusal from a file is a worse way to learn it. */
              if (words.length < 2) {
                setError('A group needs at least two words to mean anything.')
                return
              }
              void save(
                { ...tuning, synonyms: [...tuning.synonyms, words] },
                `${words.join(', ')} now mean each other.`,
              ).then(() => setNewGroup(''))
            }}
          >
            <label className="library__field">
              <span>Words that mean each other</span>
              <input
                className="library__alt"
                value={newGroup}
                placeholder="sso, saml, single sign-on"
                disabled={busy}
                onChange={(event) => setNewGroup(event.target.value)}
              />
            </label>
            <button className="btn btn--tinted" type="submit" disabled={busy || !newGroup.trim()}>
              {busy ? <Icon name="spinner" size={13} /> : <Icon name="plus" size={13} />}
              Add the group
            </button>
          </form>
        )}
      </section>

      <section className="home__card home__card--wide" data-search-aliases>
        <h2>One search means another</h2>
        <p className="home__quiet">
          One direction only: "pricing" can mean "plans" without every search for plans also meaning
          pricing. Pulse writes one of these each time somebody teaches search a word from a
          question that found nothing — this is where they can be read back, corrected and removed.
        </p>

        {aliases.length === 0 ? (
          <p className="nothing">Nothing redirected yet.</p>
        ) : (
          <ul className="vars__list">
            {aliases.map(([from, to]) => (
              <li className="vars__row" key={from} data-alias={from}>
                <div className="vars__line">
                  <code className="vars__name">{from}</code>
                  <span className="vars__meta">finds what “{to}” finds</span>
                  {!readOnly && (
                    <button
                      className="btn btn--ghost btn--icon btn--sm"
                      disabled={busy}
                      data-tip={`Remove the alias "${from}"`}
                      onClick={() => setDropping(`alias:${from}`)}
                    >
                      <Icon name="trash" size={13} label={`Remove ${from}`} />
                    </button>
                  )}
                </div>
                {dropping === `alias:${from}` && (
                  <Confirm
                    says={`Readers typing “${from}” go back to whatever that word finds on its own, which may be nothing. No page changes.`}
                    does="Remove it"
                    cancel="Keep it"
                    busy={busy}
                    onYes={() => {
                      setDropping(null)
                      const next = { ...tuning.aliases }
                      delete next[from]
                      void save({ ...tuning, aliases: next }, `“${from}” means itself again.`)
                    }}
                    onNo={() => setDropping(null)}
                  />
                )}
              </li>
            ))}
          </ul>
        )}

        {!readOnly && (
          <form
            className="vars__add"
            onSubmit={(event) => {
              event.preventDefault()
              const from = newAliasFrom.trim().toLowerCase()
              const to = newAliasTo.trim().toLowerCase()
              if (from === '' || to === '') return
              if (from === to) {
                setError('That points at itself. An alias has to lead somewhere else.')
                return
              }
              void save(
                { ...tuning, aliases: { ...tuning.aliases, [from]: to } },
                `“${from}” now finds what “${to}” finds.`,
              ).then(() => {
                setNewAliasFrom('')
                setNewAliasTo('')
              })
            }}
          >
            <label className="library__field">
              <span>When somebody searches for</span>
              <input
                className="library__alt"
                value={newAliasFrom}
                placeholder="pricing"
                disabled={busy}
                onChange={(event) => setNewAliasFrom(event.target.value)}
              />
            </label>
            <label className="library__field">
              <span>Find what this finds</span>
              <input
                className="library__alt"
                value={newAliasTo}
                placeholder="plans"
                disabled={busy}
                onChange={(event) => setNewAliasTo(event.target.value)}
              />
            </label>
            <button
              className="btn btn--tinted"
              type="submit"
              disabled={busy || !newAliasFrom.trim() || !newAliasTo.trim()}
            >
              {busy ? <Icon name="spinner" size={13} /> : <Icon name="plus" size={13} />}
              Add it
            </button>
          </form>
        )}
      </section>

      <section className="home__card home__card--wide" data-search-pins>
        <h2>Searches with a known answer</h2>
        <p className="home__quiet">
          For the handful of searches where you already know the right page, whatever the ranking
          thinks. Pinned pages come first, in the order they are added; everything else follows.
        </p>

        {pinned.length === 0 ? (
          <p className="nothing">Nothing pinned yet.</p>
        ) : (
          <ul className="vars__list">
            {pinned.map(([query, paths]) => (
              <li className="vars__row" key={query} data-pin={query}>
                <div className="vars__line">
                  <code className="vars__name">{query}</code>
                  <span className="vars__meta">{paths.map(nameOf).join(' then ')}</span>
                  {!readOnly && (
                    <button
                      className="btn btn--ghost btn--icon btn--sm"
                      disabled={busy}
                      data-tip={`Unpin "${query}"`}
                      onClick={() => {
                        const next = { ...tuning.pinned }
                        delete next[query]
                        void save(
                          { ...tuning, pinned: next },
                          `“${query}” is ranked like any other search now.`,
                        )
                      }}
                    >
                      <Icon name="trash" size={13} label={`Unpin ${query}`} />
                    </button>
                  )}
                </div>
                {/* A pin naming a page that is no longer here does nothing, and
                    says nothing on the site — so it is said here. The lint
                    refuses the same thing at publish; this is the earlier,
                    kinder half of the same sentence. */}
                {paths.some((path) => !pages.some((page) => page.path === path)) && (
                  <p className="home__quiet" data-pin-missing={query}>
                    <Icon name="alert" size={13} /> One of those pages is not here any more, so this
                    pin does nothing.
                  </p>
                )}
              </li>
            ))}
          </ul>
        )}

        {!readOnly && (
          <form
            className="vars__add"
            onSubmit={(event) => {
              event.preventDefault()
              const query = newPinQuery.trim().toLowerCase()
              if (query === '' || newPinPage === '') return
              const already = tuning.pinned[query] ?? []
              void save(
                {
                  ...tuning,
                  pinned: {
                    ...tuning.pinned,
                    [query]: already.includes(newPinPage) ? already : [...already, newPinPage],
                  },
                },
                `${nameOf(newPinPage)} comes first for “${query}”.`,
              ).then(() => {
                setNewPinQuery('')
                setNewPinPage('')
              })
            }}
          >
            <label className="library__field">
              <span>When somebody searches for</span>
              <input
                className="library__alt"
                value={newPinQuery}
                placeholder="sso"
                disabled={busy}
                onChange={(event) => setNewPinQuery(event.target.value)}
              />
            </label>
            {pagePicker(newPinPage, setNewPinPage, 'Put this page first')}
            <button
              className="btn btn--tinted"
              type="submit"
              disabled={busy || !newPinQuery.trim() || !newPinPage}
            >
              {busy ? <Icon name="spinner" size={13} /> : <Icon name="plus" size={13} />}
              Pin it
            </button>
          </form>
        )}
      </section>

      <section className="home__card home__card--wide" data-search-boosts>
        <h2>Pages worth more, or less</h2>
        <p className="home__quiet">
          A weight applies to every search rather than to one: 2 means this page counts double, 0.5
          means half. It multiplies a page's score, so a page can sink to the bottom and can never
          disappear — leaving results is what <code>noindex</code> is for.
        </p>

        {boosts.length === 0 ? (
          <p className="nothing">Every page is weighted the same.</p>
        ) : (
          <ul className="vars__list">
            {boosts.map(([path, weight]) => (
              <li className="vars__row" key={path} data-boost={path}>
                <div className="vars__line">
                  <code className="vars__name">{nameOf(path)}</code>
                  <span className="vars__meta">
                    counts {weight}× {weight > 1 ? 'more' : 'less'} than an ordinary page
                  </span>
                  {!readOnly && (
                    <button
                      className="btn btn--ghost btn--icon btn--sm"
                      disabled={busy}
                      data-tip={`Weight ${nameOf(path)} normally again`}
                      onClick={() => {
                        const next = { ...tuning.boost }
                        delete next[path]
                        void save(
                          { ...tuning, boost: next },
                          `${nameOf(path)} counts the same as every other page now.`,
                        )
                      }}
                    >
                      <Icon name="trash" size={13} label={`Reset ${nameOf(path)}`} />
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}

        {!readOnly && (
          <form
            className="vars__add"
            onSubmit={(event) => {
              event.preventDefault()
              if (newBoostPage === '') return
              const weight = Number(newBoostWeight)
              /* Between a tenth and ten, which is what the format allows: past
                 that a weight stops ranking a page and starts replacing the
                 ranking. Said here so the number can be corrected in the field
                 it was typed in. */
              if (!Number.isFinite(weight) || weight < 0.1 || weight > 10) {
                setError('A weight is between 0.1 and 10. Past that it replaces the ranking.')
                return
              }
              void save(
                { ...tuning, boost: { ...tuning.boost, [newBoostPage]: weight } },
                `${nameOf(newBoostPage)} counts ${weight}× now.`,
              ).then(() => {
                setNewBoostPage('')
                setNewBoostWeight('2')
              })
            }}
          >
            {pagePicker(newBoostPage, setNewBoostPage, 'Page')}
            <label className="library__field">
              <span>Counts this much</span>
              {/*
                No `min`/`max`: those refuse with the browser's own bubble, and
                every other refusal on this screen is a sentence in the
                product's voice at the top of it. Two refusal shapes on one
                form is the thing this redesign has spent its time removing.
                The range is still enforced — below, and again by the format.
              */}
              <input
                className="library__alt"
                type="number"
                step="0.1"
                value={newBoostWeight}
                disabled={busy}
                onChange={(event) => setNewBoostWeight(event.target.value)}
              />
            </label>
            <button className="btn btn--tinted" type="submit" disabled={busy || !newBoostPage}>
              {busy ? <Icon name="spinner" size={13} /> : <Icon name="plus" size={13} />}
              Set the weight
            </button>
          </form>
        )}
      </section>

      {/*
        The file itself, for the person who would rather write the YAML — the
        same door the glossary and the style guide keep. It is the format, and
        hiding it would be the opposite of what this product promises about
        content.
      */}
      <p className="home__quiet">
        This is a file on the draft, like every page.{' '}
        <button className="linkish" data-open-search-file onClick={() => onOpenPage(SEARCH)}>
          Open search.yaml
        </button>{' '}
        to write it by hand.
      </p>
    </div>
  )
}
